<template>
    <div class="e-card e-rounding shadow-sm">
        <h1 class="h3">{{$t('api.title')}}</h1>
        <Loader v-if="loading" />

        <div v-if="!loading">
            <p class="text-muted mb-4">
                {{$t('api.info')}}
                {{$t('api.documentationLink')}} <a href="https://api.elementarypos.com" target="_blank">{{$t('api.documentation')}}</a>.
            </p>

            <div class="api-section">
                <div class="api-section__header">
                    <i class="fas fa-key api-section__icon"></i>
                    <h2 class="h5 mb-0">{{$t('api.keyLabel')}}</h2>
                    <span class="badge ml-2" :class="apiEnabled ? 'badge-success' : 'badge-secondary'">
                        {{ apiEnabled ? $t('api.badge.enabled') : $t('api.badge.disabled') }}
                    </span>
                </div>

                <div class="api-section__body">
                    <div v-if="apiEnabled" class="api-key-row">
                        <input v-model="apiKey" readonly class="form-control api-key-input" />
                        <button @click="copyApiKey()" class="btn btn-outline-secondary ml-2" title="Copy">
                            <i class="fas fa-copy"></i>
                        </button>
                        <span v-if="apiKeyCopied" class="text-success ml-2">
                            <i class="fas fa-check"></i> {{$t('api.keyCopied')}}
                        </span>
                    </div>
                    <p v-else class="text-muted mb-3"><i class="fas fa-lock mr-1"></i>{{$t('api.disabled')}}</p>

                    <button v-if="!apiEnabled" @click="enable()" class="btn btn-primary">
                        <i v-if="updateApiKey" class="fas fa-spinner fa-spin mr-2"></i>
                        <i v-else class="fas fa-toggle-on mr-2"></i>
                        {{$t('button.enableAPI')}}
                    </button>
                    <button v-else @click="disable()" class="btn btn-outline-danger btn-sm mt-3">
                        <i v-if="updateApiKey" class="fas fa-spinner fa-spin mr-2"></i>
                        <i v-else class="fas fa-toggle-off mr-2"></i>
                        {{$t('button.disableAPI')}}
                    </button>

                    <div v-if="apiEnabled" class="api-feedback mt-4">
                        <p class="mb-1">{{$t('api.info3')}}</p>
                        <p class="mb-0">{{$t('api.info2')}} <a href="mailto:api@elementarypos.com"><b>api@elementarypos.com</b></a>.</p>
                    </div>
                </div>
            </div>

            <div class="api-section">
                <div class="api-section__header">
                    <i class="fab fa-wordpress api-section__icon"></i>
                    <h2 class="h5 mb-0">{{$t('api.wordpress.title')}}</h2>
                </div>
                <div class="api-section__body">
                    <p class="mb-2">{{$t('api.wordpress.pluginInfo')}}</p>
                    <ul class="list-unstyled mb-0">
                        <li class="mb-1">
                            <i class="fas fa-plug mr-2 text-muted"></i>
                            <a href="https://wordpress.org/plugins/elementary-pos-for-woocommerce/" target="_blank">WooCommerce Plugin</a>
                        </li>
                        <li>
                            <i class="fas fa-plug mr-2 text-muted"></i>
                            <a href="https://wordpress.org/plugins/elementary-pos/" target="_blank">{{$t('api.wordpress.plugin')}}</a>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="api-section">
                <div class="api-section__header">
                    <i class="fas fa-bolt api-section__icon"></i>
                    <h2 class="h5 mb-0">{{$t('api.webhook.title')}}</h2>
                    <span v-if="webhookState" class="badge ml-2" :class="webhookStatusBadgeClass">
                        {{ webhookStatusLabel }}
                    </span>
                </div>

                <div class="api-section__body">
                    <p class="text-muted mb-3">{{$t('api.webhook.description')}}</p>

                    <div class="form-group">
                        <label class="font-weight-600">{{$t('api.webhook.urlLabel')}}</label>
                        <input v-model="webhookURI" placeholder="https://mysystem.com/pos" class="form-control webhook-url-input" />
                    </div>

                    <div v-if="webhookURI" class="api-webhook-example mb-3">
                        <small class="text-muted">
                            {{$t('api.webhook.urlInfo')}}
                            <code>{{ webhookURI }}?<b>type</b>=receipt_created&amp;<b>id</b>=RECEIPT_UUID</code>
                        </small>
                        <br>
                        <small class="text-muted">
                            {{$t('api.webhook.urlInfo2')}} <a href="https://api.elementarypos.com" target="_blank">{{$t('api.webhook.documentation')}}</a>.
                        </small>
                    </div>

                    <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                        <button v-show="!webhookEnabled" @click="enableWebhook()" class="btn btn-primary">
                            <i class="fas fa-toggle-on mr-2"></i>{{$t('api.webhook.enable')}}
                        </button>
                        <button v-show="webhookEnabled" @click="disableWebhook()" class="btn btn-outline-danger">
                            <i class="fas fa-toggle-off mr-2"></i>{{$t('api.webhook.disable')}}
                        </button>

                        <div v-if="webhookState && webhookState !== 'off'" class="webhook-status ml-2">
                            <span class="webhook-status__dot" :class="'webhook-status__dot--' + webhookState"></span>
                            <span :class="webhookStatusTextClass">{{ webhookStatusLabel }}</span>
                        </div>
                    </div>

                    <div v-if="webhookMessage" class="alert alert-danger mt-3 mb-0 py-2">
                        <i class="fas fa-exclamation-triangle mr-2"></i>{{ webhookMessage }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Loader from "@/components/Loader"

    export default {
        name: 'Api',
        components: { Loader },

        data: function () {
            return {
                loading: true,
                updateApiKey: false,
                apiKey: null,
                apiEnabled: false,
                apiKeyCopied: false,
                webhookURI : '',
                webhookState : '',
                webhookMessage: '',
                webhookEnabled: false,
                updateWebhook: false
            }
        },

        computed: {
            webhookStatusBadgeClass() {
                const map = { initialized: 'badge-info', healthy: 'badge-success', warning: 'badge-warning', error: 'badge-danger', off: 'badge-secondary' };
                return map[this.webhookState] || 'badge-secondary';
            },
            webhookStatusTextClass() {
                const map = { initialized: 'text-info', healthy: 'text-success', warning: 'text-warning', error: 'text-danger', off: 'text-muted' };
                return map[this.webhookState] || 'text-muted';
            },
            webhookStatusLabel() {
                const key = 'api.webhook.status.' + this.webhookState;
                return this.$t(key);
            }
        },

        mounted: function () {
          this.refresh();
        },

        methods: {
            refresh: function () {
                const vm = this;
                vm.loading = true;
                this.$http.post('/company/get-api-key')
                    .then(function (response) {
                        vm.apiKey = response.data.apiKey;
                        vm.apiEnabled = response.data.enabled;
                        vm.loading = false;
                    });
                vm.refreshWebhook();
            },
            copyApiKey: function () {
                const vm = this;
                navigator.clipboard.writeText(vm.apiKey).then(function() {
                    vm.apiKeyCopied = true;
                    setTimeout(function() {
                        vm.apiKeyCopied = false;
                    }, 2000);
                });
            },
            setApi: function (enable) {
                const vm = this;
                vm.updateApiKey = true;
                this.$http.post('/company/enable-api-key', {enable:enable})
                    .then(function () {
                        vm.updateApiKey = false;
                        vm.refresh();
                    },function () {
                        vm.updateApiKey = false;
                    });
            },
            enable: function () {
              this.setApi(true);
            },
            disable: function () {
              this.$modal.cs_confirm(this.$t("warning.deactivateApi"),()=>{
                 this.setApi(false);
               });
            },
            enableWebhook: function () {
              const vm = this;
              if (!vm.webhookURI || vm.webhookURI.trim() === '') {
                this.$modal.cs_alert(this.$t('api.webhook.urlRequired'));
                return;
              }
              vm.updateWebhook = true;
              this.$http.post('/webhook/enable-webhook', {uri:vm.webhookURI})
                  .then(function () {
                    vm.updateWebhook = false;
                    vm.refreshWebhook();
                  },function () {
                    vm.updateWebhook = false;
                    vm.refreshWebhook();
                  });
            },
            disableWebhook: function () {
             const vm = this;
              this.$modal.cs_confirm(this.$t("api.webhook.confirmDisable"),()=>{
                vm.updateWebhook = true;
                this.$http.post('/webhook/disable-webhook', {})
                    .then(function () {
                      vm.updateWebhook = false;
                      vm.refreshWebhook();
                    }, function () {
                      vm.updateWebhook = false;
                      vm.refreshWebhook();
                    });
              });
            },
            refreshWebhook: function() {
              const vm = this;
              this.$http.post('/webhook/get-webhook-settings', {})
                  .then(function (response) {
                    vm.webhookURI = response.data.uri;
                    vm.webhookState = response.data.state;
                    vm.webhookMessage = response.data.message;
                    vm.webhookEnabled = response.data.enabled;

                    if(vm.webhookState === 'initialized') {
                      setTimeout(function() { vm.refreshWebhook(); }, 3000);
                    }
                  });
            }
        }
    }
</script>

<style scoped>
.api-section {
    border: 1px solid #e9ecef;
    border-radius: .5rem;
    margin-bottom: 1.5rem;
    overflow: hidden;
}

.api-section__header {
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    padding: .875rem 1.25rem;
    border-bottom: 1px solid #e9ecef;
}

.api-section__icon {
    color: #6c757d;
    margin-right: .625rem;
    width: 1rem;
    text-align: center;
}

.api-section__body {
    padding: 1.25rem;
}

.api-key-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: .5rem;
}

.api-key-input {
    max-width: 400px;
    font-family: monospace;
    font-size: .875rem;
    background-color: #f8f9fa;
}

.webhook-url-input {
    max-width: 400px;
}

.api-feedback {
    background-color: #f8f9fa;
    border-left: 3px solid #dee2e6;
    padding: .75rem 1rem;
    border-radius: 0 .25rem .25rem 0;
    font-size: .9rem;
    color: #6c757d;
}

.webhook-status {
    display: flex;
    align-items: center;
    gap: 6px;
}

.webhook-status__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
}

.webhook-status__dot--initialized { background-color: #17a2b8; }
.webhook-status__dot--healthy     { background-color: #28a745; }
.webhook-status__dot--warning     { background-color: #ffc107; }
.webhook-status__dot--error       { background-color: #dc3545; }
.webhook-status__dot--off         { background-color: #6c757d; }

.font-weight-600 {
    font-weight: 600;
}

.api-webhook-example {
    background-color: #f8f9fa;
    border-radius: .25rem;
    padding: .625rem .875rem;
}
</style>
